import type { BotDifficulty } from '@cribbage/bots';
import { gameFormatSchema } from '@cribbage/protocol';
import type { GameFormat } from './sessionStore.js';

/**
 * The last game a player started from Play, so the home can offer it again.
 *
 * The home's Play again door replays one configuration in one tap: the same
 * game, the same seats and the same opponents. Play setup records it when a
 * player launches a game, and nothing else writes it, so the door only ever
 * offers a game this player has actually chosen. A first launch has none, and
 * the home draws Play alone.
 *
 * Three launches can be replayed: a local game against bots, a local
 * pass-and-play game, and an online quick match of either game. A private
 * table cannot: its code belongs to one sitting.
 */

export type LastGameVariant = 'standard' | 'cross';

export type LastGameSeats = 2 | 3 | 4;

export type LastGame =
  | {
      readonly kind: 'bot';
      readonly variant: LastGameVariant;
      readonly players: LastGameSeats;
      readonly difficulty: BotDifficulty;
      readonly crib: boolean;
      /** False for a standard four-player free-for-all; four seats are partners otherwise. */
      readonly teams?: false;
    }
  | {
      readonly kind: 'pass';
      readonly variant: LastGameVariant;
      readonly players: LastGameSeats;
      readonly crib: boolean;
    }
  | {
      readonly kind: 'queue';
      readonly variant: LastGameVariant;
      readonly format: GameFormat;
      readonly bots: boolean;
    };

/**
 * Where a launch goes. Play setup's own links are built here too, so Play
 * again opens exactly the address the player's first launch did.
 */
export function lastGameHref(game: LastGame): string {
  if (game.kind === 'queue') {
    const mode = game.variant === 'cross' ? '&mode=cross' : '';
    return `/queue?format=${game.format}&bots=${game.bots ? '1' : '0'}${mode}`;
  }
  const params = [
    `mode=${game.kind}`,
    `variant=${game.variant}`,
    `players=${String(game.players)}`,
  ];
  if (game.kind === 'bot') params.push(`difficulty=${game.difficulty}`);
  if (game.kind === 'bot' && game.teams === false) params.push('teams=0');
  if (game.crib) params.push('crib=1');
  return `/local?${params.join('&')}`;
}

const VARIANTS: readonly unknown[] = ['standard', 'cross'] satisfies LastGameVariant[];
const SEATS: readonly unknown[] = [2, 3, 4] satisfies LastGameSeats[];
const DIFFICULTIES: readonly unknown[] = [
  'easy',
  'medium',
  'hard',
  'expert',
] satisfies BotDifficulty[];

/**
 * A stored record, checked field by field, or null.
 *
 * It comes back from the device's storage, which an older build or a hand edit
 * may have left in another shape. A record the table cannot open is not one to
 * offer, so anything short of a complete launch reads as no last game.
 */
export function lastGameOrNull(value: unknown): LastGame | null {
  if (value === null || typeof value !== 'object') return null;
  const game = value as Record<string, unknown>;
  if (game.kind === 'queue') {
    const format = gameFormatSchema.safeParse(game.format);
    if (!format.success || typeof game.bots !== 'boolean') return null;
    // A match queued before online Cross existed carries no variant: it was Cribbage.
    const variant = game.variant ?? 'standard';
    if (!VARIANTS.includes(variant)) return null;
    return {
      kind: 'queue',
      variant: variant as LastGameVariant,
      format: format.data,
      bots: game.bots,
    };
  }
  if (game.kind !== 'bot' && game.kind !== 'pass') return null;
  if (!VARIANTS.includes(game.variant) || !SEATS.includes(game.players)) return null;
  if (typeof game.crib !== 'boolean') return null;
  const variant = game.variant as LastGameVariant;
  const players = game.players as LastGameSeats;
  if (game.kind === 'pass') return { kind: 'pass', variant, players, crib: game.crib };
  if (!DIFFICULTIES.includes(game.difficulty)) return null;
  const difficulty = game.difficulty as BotDifficulty;
  const teams = game.teams === false && variant === 'standard' && players === 4;
  return {
    kind: 'bot',
    variant,
    players,
    difficulty,
    crib: game.crib,
    ...(teams ? { teams: false } : {}),
  };
}
